// color-exempt-file — retokenisation 5728fe509 : classes dark:bg-[#hex] ajoutees pour preserver le rendu dark historique (voir memoire ui-themes)
import React, { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  Navigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Sparkles } from "lucide-react";
import { SimpleProviders } from "@core/components/SimpleProviders";
import { ErrorBoundary } from "@core/components/ErrorBoundary";
import { AdminGuard } from "./components/admin/AdminGuard";
import { LangQuerySync } from "./hooks/useLangQuerySync";
import { SupportDashboard } from "@core/components/admin/support/SupportDashboard";
import { SupportBotDashboard } from "@core/components/admin/support/SupportBotDashboard";
import { AnalyticsDashboard } from "./pages/admin/AnalyticsDashboard";
import { PulseDashboard } from "./pages/admin/PulseDashboard";
import { UsersAdmin } from "./pages/admin/UsersAdmin";
import { UserTimeline } from "./pages/admin/UserTimeline";
import AdminLoginPage from "./pages/AdminLoginPage";
import { SupportFeedbackPage } from "./pages/SupportFeedbackPage";

// Imports directs pour éviter les problèmes de lazy loading
import DatePollsLanding from "./pages/LandingPage";
import DatePollsAbout from "./pages/About";
import { VS_PAGES } from "./pages/vsPages";
import AiPage from "./pages/AiPage";
import NewPollFromLink from "./pages/NewPollFromLink";
import FinalDateUnsubscribe from "./pages/FinalDateUnsubscribe";
import DateCreator from "./pages/DateCreator";
import DatePollsDashboard from "./pages/Dashboard";
import DatePollCreationWorkspace from "./components/DatePollCreationWorkspace";
import FormPollCreationWorkspace from "./components/FormPollCreationWorkspace";
import Vote from "./pages/Vote";
import Results from "./pages/Results";
import PollInvitations from "./pages/PollInvitations";
import DateLegal from "./pages/Legal";
import Pricing from "./pages/Pricing";
import Documentation from "./pages/Documentation";
import NotFound from "./components/NotFound";
import Contact from "./pages/Contact";
import DocsPage from "./pages/DocsPage";
import JournalSimple from "./pages/JournalSimple";
import DataControl from "./pages/DataControl";
import { SupportPage } from "./pages/SupportPage";
import GuidePage from "./pages/GuidePage";
import GuidesIndex from "./pages/GuidesIndex";
import GroupsPageFinal from "./pages/GroupsPageFinal";
import { TrialStart } from "@core/pages/TrialStart";
import AccountSubscription from "@core/pages/account/AccountSubscription";
import AccountInvoices from "@core/pages/account/AccountInvoices";
import AccountSecurity from "@core/pages/account/AccountSecurity";
import AccountPreferences from "@core/pages/account/AccountPreferences";
import AccountApiKeys from "@core/pages/account/AccountApiKeys";
import AuthCallback from "./pages/AuthCallback";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import PublicProfile from "./pages/PublicProfile";
import AccountPersonalPage from "./pages/AccountPersonalPage";
import AccountAppearance from "./pages/AccountAppearance";
import { AuthRequired } from "@core/components/AuthRequired";
import { SEOHead } from "./components/SEOHead";
import { ProductHeader } from "@core/components/layout/products/ProductHeader";
import { ProductSidebar } from "@core/components/layout/products/ProductSidebar";
import { TestIdentitySwitcher } from "@core/components/dev/TestIdentitySwitcher";
import { InstallAppBanner } from "./components/InstallAppBanner";

// Import Roadmap
const RoadmapPage = lazy(() => import("@core/pages/roadmap/RoadmapPage"));
const FeatureRequestDetailPage = lazy(
  () => import("@core/pages/roadmap/FeatureRequestDetailPage"),
);
// Dashboard admin des feature requests (changement de status, édition
// admin_notes / priority / target_version). Lazy pour isoler.
const RoadmapAdminDashboard = lazy(() =>
  import("@core/components/admin/AdminDashboard").then((m) => ({
    default: m.AdminDashboard,
  })),
);

const AccountLayout = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation();
  return (
    <>
      <SEOHead title={t("seo_title_account")} noIndex />
      <AuthRequired
        header={<ProductHeader productType="date" showAuth={true} />}
      >
        <div className="min-h-screen-safe bg-background">
          <ProductHeader productType="date" showAuth={true} />
          <div className="flex">
            <ProductSidebar productType="date" />
            <div className="flex-1 min-w-0">{children}</div>
          </div>
        </div>
      </AuthRequired>
    </>
  );
};

const PollSlugRedirect = () => {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/poll/${slug}/vote`} replace />;
};

// URL courte d'outreach : /try → page d'accueil plan Gratuit, en
// préservant les paramètres UTM (l'attribution est capturée au boot,
// cf. lib/utmTracking). `home=1` force l'affichage de la landing et
// empêche le rebond visiteur-récurrent vers /create (LandingPage.tsx),
// pour que les UTM restent visibles dans l'URL finale. Redirection
// instantanée, pas de loader.
const TryRedirect = () => {
  const [params] = useSearchParams();
  params.set("plan", "gratuit");
  params.set("home", "1");
  return <Navigate to={`/?${params.toString()}`} replace />;
};

const LoadingSpinner = () => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center py-8">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      <span className="ml-2 text-muted-foreground">{t("loading")}</span>
    </div>
  );
};

export default function App() {
  const { t } = useTranslation(["common"]);
  console.log(
    "🚀 Date Polls App rendering - VERSION 0.4.8 (Unified SimpleProviders)",
  );

  return (
    <BrowserRouter
      basename={
        window.location.pathname === "/date" ||
        window.location.pathname.startsWith("/date/")
          ? "/date"
          : "/"
      }
    >
      <SimpleProviders>
        {/* En flux normal (pas fixed) : pousse le header vers le bas plutôt
            que de le recouvrir, header n'étant pas lui-même sticky. */}
        <InstallAppBanner />
        {/* Resynchronise ?lang=xx sur la langue active, y compris en
            navigation SPA (i18next ne détecte qu'au 1er chargement). issue 879 */}
        <LangQuerySync />
        {/* Suspense local à l'app date-polls : absorbe les suspensions des
            routes lazy() (roadmap, pricing, etc.) sans faire disparaître
            l'header ou le layout global. Cf. Docs/ARCHITECTURE/
            2026-04-15-ROADMAP-FRAGILITE-ANALYSE.md §P0.1 */}
        <Suspense fallback={<LoadingSpinner />}>
          <ErrorBoundary>
            <Routes>
              <Route path="/" element={<DatePollsLanding />} />
              <Route path="/try" element={<TryRedirect />} />
              <Route path="/roadmap" element={<RoadmapPage />} />
              <Route
                path="/roadmap/:id"
                element={<FeatureRequestDetailPage />}
              />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/about" element={<DatePollsAbout />} />
              {Object.entries(VS_PAGES).map(([slug, Page]) => (
                <Route key={slug} path={`/vs/${slug}`} element={<Page />} />
              ))}
              <Route path="/ai" element={<AiPage />} />
              {/* Versions anglaises pré-rendues, cf. seo/enRoutes.ts */}
              <Route path="/en" element={<DatePollsLanding />} />
              <Route path="/en/ai" element={<AiPage />} />
              <Route path="/en/pricing" element={<Pricing />} />
              <Route path="/en/guides" element={<GuidesIndex />} />
              <Route path="/en/guides/:slug" element={<GuidePage />} />
              {Object.entries(VS_PAGES).map(([slug, Page]) => (
                <Route
                  key={`en-${slug}`}
                  path={`/en/vs/${slug}`}
                  element={<Page />}
                />
              ))}
              <Route path="/new" element={<NewPollFromLink />} />
              <Route
                path="/notifications/date/stop"
                element={<FinalDateUnsubscribe />}
              />
              <Route path="/create" element={<DateCreator />} />
              <Route path="/dashboard" element={<DatePollsDashboard />} />
              <Route path="/tableau-de-bord" element={<DatePollsDashboard />} />
              <Route
                path="/workspace/date"
                element={<DatePollCreationWorkspace />}
              />
              <Route
                path="/workspace/form"
                element={<FormPollCreationWorkspace />}
              />
              {/* Public profile page — must come before /poll/:slug.
                  React Router v6 can't match a param glued to a literal
                  prefix within the same segment (/@:slug never matches
                  /@foo), so this is registered as a plain /:handle catch
                  and PublicProfile strips the leading @ itself. Static
                  routes rank above dynamic ones in v6, so this doesn't
                  shadow any of the named routes below. */}
              <Route path="/:handle" element={<PublicProfile />} />
              <Route path="/poll/:slug" element={<PollSlugRedirect />} />
              <Route path="/poll/:slug/vote" element={<Vote />} />
              <Route path="/poll/:slug/results" element={<Results />} />
              <Route
                path="/poll/:slug/invitations"
                element={<PollInvitations />}
              />
              <Route path="/data-control" element={<DataControl />} />

              {/* Documentation et Pricing */}
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/docs" element={<Documentation />} />
              <Route path="/date/docs" element={<Documentation />} />
              <Route path="/journal" element={<JournalSimple />} />
              <Route path="/groupes" element={<GroupsPageFinal />} />
              <Route path="/essai" element={<TrialStart />} />
              <Route
                path="/guide"
                element={
                  <Navigate to="/guides/fonctionnalites-avancees" replace />
                }
              />
              <Route path="/guides" element={<GuidesIndex />} />
              <Route path="/guides/:slug" element={<GuidePage />} />

              {/* Légal */}
              <Route path="/terms" element={<DateLegal />} />
              <Route path="/privacy" element={<DateLegal />} />
              <Route path="/legal" element={<DateLegal />} />
              <Route path="/docs/:docPath" element={<DateLegal />} />
              <Route path="/docs/ia-policy" element={<DateLegal />} />

              {/* Pages Compte — protégées par auth + header global */}
              <Route
                path="/account/overview"
                element={<Navigate to="/account/subscription" replace />}
              />
              <Route
                path="/account/subscription"
                element={
                  <AccountLayout>
                    <AccountSubscription />
                  </AccountLayout>
                }
              />
              <Route
                path="/account/invoices"
                element={
                  <AccountLayout>
                    <AccountInvoices />
                  </AccountLayout>
                }
              />
              <Route
                path="/account/security"
                element={
                  <AccountLayout>
                    <AccountSecurity />
                  </AccountLayout>
                }
              />
              <Route
                path="/account/preferences"
                element={
                  <AccountLayout>
                    <AccountPreferences />
                  </AccountLayout>
                }
              />
              <Route
                path="/account/api-keys"
                element={
                  <AccountLayout>
                    <AccountApiKeys />
                  </AccountLayout>
                }
              />
              <Route
                path="/account/personal-page"
                element={
                  <AccountLayout>
                    <AccountPersonalPage />
                  </AccountLayout>
                }
              />
              <Route
                path="/account/appearance"
                element={
                  <AccountLayout>
                    <AccountAppearance />
                  </AccountLayout>
                }
              />

              {/* Auth callback OAuth */}
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route
                path="/auth/forgot-password"
                element={<ForgotPasswordPage />}
              />
              <Route
                path="/auth/reset-password"
                element={<ResetPasswordPage />}
              />

              {/* Support feedback */}
              <Route
                path="/support/feedback/:ticketId"
                element={<SupportFeedbackPage />}
              />

              {/* Admin */}
              <Route path="/login" element={<AdminLoginPage />} />
              <Route path="/auth/signin" element={<AdminLoginPage />} />
              <Route
                path="/admin"
                element={
                  <AdminGuard>
                    <div className="min-h-screen-safe bg-background dark:bg-[#030712] text-foreground">
                      <header className="relative z-10 border-b border-border bg-background/80 backdrop-blur-md">
                        <div className="max-w-7xl mx-auto px-6">
                          <div className="flex justify-between items-center h-16">
                            <Link
                              to="/"
                              className="flex items-center gap-3 cursor-pointer"
                            >
                              <div className="w-8 h-8 rounded bg-primary flex items-center justify-center">
                                <Sparkles className="w-4 h-4 text-primary-foreground" />
                              </div>
                              <span className="text-md font-semibold text-foreground">
                                DooDates Admin
                              </span>
                            </Link>
                            <nav className="flex items-center gap-4">
                              <Link
                                to="/admin"
                                className="text-xs text-foreground hover:text-primary transition-colors font-medium"
                              >
                                Support
                              </Link>
                              <Link
                                to="/admin/support-bot"
                                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                              >
                                {t("admin:support_bot.title")}
                              </Link>
                              <Link
                                to="/admin/roadmap"
                                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                              >
                                Roadmap
                              </Link>
                              <Link
                                to="/admin/analytics"
                                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                              >
                                Analytics
                              </Link>
                              <Link
                                to="/admin/pulse"
                                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                              >
                                Pulse
                              </Link>
                              <Link
                                to="/admin/users"
                                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                              >
                                Users
                              </Link>
                              <Link
                                to="/"
                                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                              >
                                {t("common:exit_admin")}
                              </Link>
                            </nav>
                          </div>
                        </div>
                      </header>
                      <main>
                        <div className="animate-in fade-in duration-700">
                          <SupportDashboard />
                        </div>
                      </main>
                    </div>
                  </AdminGuard>
                }
              />
              <Route
                path="/admin/roadmap"
                element={
                  <AdminGuard>
                    <div className="min-h-screen-safe bg-background dark:bg-[#030712] text-foreground">
                      <header className="relative z-10 border-b border-border bg-background/80 backdrop-blur-md">
                        <div className="max-w-7xl mx-auto px-6">
                          <div className="flex justify-between items-center h-16">
                            <Link
                              to="/"
                              className="flex items-center gap-3 cursor-pointer"
                            >
                              <div className="w-8 h-8 rounded bg-primary flex items-center justify-center">
                                <Sparkles className="w-4 h-4 text-primary-foreground" />
                              </div>
                              <span className="text-md font-semibold text-foreground">
                                DooDates Admin
                              </span>
                            </Link>
                            <nav className="flex items-center gap-4">
                              <Link
                                to="/admin"
                                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                              >
                                Support
                              </Link>
                              <Link
                                to="/admin/support-bot"
                                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                              >
                                {t("admin:support_bot.title")}
                              </Link>
                              <Link
                                to="/admin/roadmap"
                                className="text-xs text-foreground hover:text-primary transition-colors font-medium"
                              >
                                Roadmap
                              </Link>
                              <Link
                                to="/admin/analytics"
                                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                              >
                                Analytics
                              </Link>
                              <Link
                                to="/admin/pulse"
                                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                              >
                                Pulse
                              </Link>
                              <Link
                                to="/admin/users"
                                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                              >
                                Users
                              </Link>
                              <Link
                                to="/"
                                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                              >
                                {t("common:exit_admin")}
                              </Link>
                            </nav>
                          </div>
                        </div>
                      </header>
                      <main>
                        <div className="animate-in fade-in duration-700">
                          <RoadmapAdminDashboard />
                        </div>
                      </main>
                    </div>
                  </AdminGuard>
                }
              />
              <Route
                path="/admin/analytics"
                element={
                  <AdminGuard>
                    <AnalyticsDashboard />
                  </AdminGuard>
                }
              />
              <Route
                path="/admin/support-bot"
                element={
                  <AdminGuard>
                    <SupportBotDashboard />
                  </AdminGuard>
                }
              />
              <Route
                path="/admin/pulse"
                element={
                  <AdminGuard>
                    <PulseDashboard />
                  </AdminGuard>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <AdminGuard>
                    <UsersAdmin />
                  </AdminGuard>
                }
              />
              <Route
                path="/admin/users/:userId"
                element={
                  <AdminGuard>
                    <UserTimeline />
                  </AdminGuard>
                }
              />
              <Route
                path="/poll/:pollId/delete"
                element={
                  <div className="p-8 text-center" data-testid="access-denied">
                    <h1 className="text-2xl font-bold text-destructive">
                      {t("common:access_denied")}
                    </h1>
                  </div>
                }
              />

              {/* Catch-all route for unknown paths */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </ErrorBoundary>
        </Suspense>
      </SimpleProviders>
      <Analytics />
      <SpeedInsights />
      {/* Rend null en prod (isTestIdentitySwitchAllowed) — voir
          TestIdentitySwitcher.tsx pour le détail du garde-fou. */}
      <TestIdentitySwitcher />
    </BrowserRouter>
  );
}
